import { Injectable, signal } from '@angular/core';
import { DateTime } from 'luxon';
import { UserDb } from 'services/user-db.service';
import { Log_WeighIn, getAverageWaist, getAverageCalipersSum } from '@cf-shared/data-model/log-weigh-in';
import { LogRecordType } from '@cf-shared/data-model/user-log';
import { ProgressChartSeries, ProgressChartDataPoint, ProgressChartAxisConfig } from 'components/cs-progress-chart/cs-progress-chart';
import { DocWatcher } from 'services/database-wrapper';
import { ProgressChartTrend } from 'components/cs-progress-chart/progress-chart-trend';
import { RUNNING_TREND_BODY_FAT_OPTIONS, runningTrendReadWindow } from '@cf-shared/running-trend';
import { buildBodyCompTrends } from './body-comp-trends';
import { LL } from 'i18n/i18n.service';
import { SettingsService } from 'services/settings.service';
import { seriesUnit } from 'components/cs-progress-chart/progress-chart-units';

/**
 * Data provider service for body composition charts.
 * Provides series configuration and data fetching for weight, waist, and skinfold measurements.
 */
@Injectable({ providedIn: 'root' })
export class ChartDataBodyCompositionService {

  // loading state signal - set to true while fetching data
  isLoading = signal(false);

  constructor(private userDb: UserDb, private settings: SettingsService) {}

  /**
   * Get series configuration for body composition chart.
   * Returns series for weight, waist, and skinfold.
   */
  getSeries(): ProgressChartSeries[] {
    // the axis unit comes from the same helper the chart converts with, so the two cannot disagree
    const unitName = (measurementTypeId: string) =>
      seriesUnit(measurementTypeId, undefined, this.settings.locale, type => this.settings.getUnitPref(type)).unitName ?? '';
    return [
      {
        id: 'weight',
        label: LL.common.measurements.weight(),
        colorVar: '--chart-purple',
        yAxisId: 'y-3-weight',
        yAxisLabel: LL.progress.axisTitles.weight({ unit: unitName('bodyweight_kg') }),
        measurementTypeId: 'bodyweight_kg'
      },
      {
        id: 'waist',
        label: LL.common.measurements.waist(),
        colorVar: '--chart-turquoise',
        yAxisId: 'y-2-waist',
        yAxisLabel: LL.progress.axisTitles.waist({ unit: unitName('bodydimensions_cm') }),
        measurementTypeId: 'bodydimensions_cm'
      },
      {
        id: 'skinfold',
        label: LL.common.measurements.skinfold(),
        colorVar: '--chart-blue',
        yAxisId: 'y-1-skinfold',
        yAxisLabel: LL.common.measurements.skinfold(),
        precision: 0.1
      }
    ];
  }

  /**
   * Get axis configuration for body composition chart.
   * Uses stacked Y-axes for separate measurement scales.
   */
  getAxisConfig(): ProgressChartAxisConfig {
    return { stacked: true };
  }

  /**
   * Fetch body composition data for the given date range.
   *
   * @param startDate - ISO date string for range start
   * @param endDate - ISO date string for range end
   * @param options - Optional configuration
   * @param options.extendedBuffer - Days to extend before startDate for trailing average calculations
   * @param options.leaveLoadingSignalAlone - Do not touch `isLoading`; for a second read beside the dots, so it never flips the page's spinner
   * @returns Array of data points with date, weight, waist, and skinfold values
   */
  async fetchDataAsync(
    startDate: string,
    endDate: string,
    watcher: DocWatcher,
    options?: { extendedBuffer?: number; leaveLoadingSignalAlone?: boolean }
  ): Promise<ProgressChartDataPoint[]> {
    const ownsLoadingSignal = !options?.leaveLoadingSignalAlone;
    if (ownsLoadingSignal) this.isLoading.set(true);

    try {
      const buffer = options?.extendedBuffer ?? 0;

      // convert ISO string dates to DateTime objects
      const startDateTime = DateTime.fromISO(startDate).startOf('day');
      const endDateTime = DateTime.fromISO(endDate).endOf('day');

      // extend start date for trailing average buffer
      const extendedStartDateTime = buffer > 0
        ? startDateTime.minus({ days: buffer })
        : startDateTime;

      // fetch weigh-in entries
      const logEntries = await this.userDb.getLogEntriesInRangeAsync(
        extendedStartDateTime,
        endDateTime,
        watcher,
        { inLogEntriesTimeZone: true }
      );

      const weighInEntries = logEntries.filter(e => e.type === LogRecordType.WeighIn) as Log_WeighIn[];

      // get gender for skinfold calculations
      const immutableState = await this.userDb.loadImmutableStateAsync(watcher);
      const gender = immutableState.gender;

      // extract data points
      const dataPoints: ProgressChartDataPoint[] = [];

      for (const entry of weighInEntries) {
        const dateISO = DateTime.fromISO(entry.time_utc).toISODate();

        const dataPoint: ProgressChartDataPoint = {
          date: dateISO
        };

        // weight in kg (canonical unit)
        if (entry.weight_kg !== undefined) {
          dataPoint['weight'] = entry.weight_kg;
        }

        // waist in cm (canonical unit)
        const waistCm = getAverageWaist(entry);
        if (waistCm !== undefined) {
          dataPoint['waist'] = waistCm;
        }

        // skinfold sum in mm (canonical unit)
        const skinfoldSum = getAverageCalipersSum(entry, gender);
        if (skinfoldSum !== undefined) {
          dataPoint['skinfold'] = skinfoldSum;
        }

        dataPoints.push(dataPoint);
      }

      // sort by date
      dataPoints.sort((a, b) => a.date.localeCompare(b.date));

      return dataPoints;
    } finally {
      if (ownsLoadingSignal) this.isLoading.set(false);
    }
  }

  /**
   * The running trend of each body composition series for a chart range (#6627). Reads its own
   * readings over `runningTrendReadWindow` for the longest window drawn (skinfolds and waist), so
   * the trend is settled for a past range and every day has the same value whichever range shows it.
   * It leaves `isLoading` alone: the dots have their own read and their own spinner.
   *
   * @param todayIso - the app's today, from `DebugService.getNowLocal()`, never the device clock
   */
  async fetchTrendsAsync(
    range: { firstDayIso: string; lastDayIso: string; todayIso: string },
    watcher: DocWatcher,
    showTunnel: boolean,
  ): Promise<ProgressChartTrend[]> {
    const read = runningTrendReadWindow(range, RUNNING_TREND_BODY_FAT_OPTIONS);
    const dataPoints = await this.fetchDataAsync(read.fromIso, read.toIso, watcher, { leaveLoadingSignalAlone: true });
    return buildBodyCompTrends(dataPoints, this.getSeries().map(series => series.id), range, showTunnel);
  }
}
